import { UnresolvedDependencyError } from './clientApiError.helpers';

const getPath = (object, fields) =>
  fields.reduce(
    (value, field) =>
      value !== null && value !== undefined ? value[field] : undefined,
    object
  );

/**
 * Resolves one dag dependency path against a step's own key and the journey's
 * stored states and responses. Implementation keys hold "@", so the path is
 * only ever split on ".".
 *
 *   Own.inputs | Own.state | Own.api.<operation>.response[.<field>...]
 *   Implementations.<key>.state | Implementations.<key>.api.<operation>.response[.<field>...]
 */
const valueForPath = (
  path,
  { ownKey, ownInputs, stepStates, stepResponses }
) => {
  const [scope, second, ...rest] = path.split('.');
  let stepKey;
  if (scope === 'Own') stepKey = ownKey;
  else if (scope === 'Implementations') stepKey = second;
  if (stepKey === undefined) return undefined;

  const [what, ...tail] = scope === 'Own' ? [second, ...rest] : rest;
  if (what === 'inputs') return scope === 'Own' ? ownInputs : undefined;
  if (what === 'state') return stepStates?.[stepKey];
  if (what !== 'api') return undefined;

  const [operation, responseWord, ...fields] = tail;
  if (responseWord !== 'response') return undefined;
  return getPath(stepResponses?.[stepKey]?.[operation], fields);
};

/**
 * Builds the request body for an outreach step by resolving each dependency path
 * declared on the dag to its runtime value. Path strings are used verbatim as
 * keys. A path that resolves to nothing is an error, not an omission.
 *
 * @returns {{key: string, value: *}[]} resolved dependencies, in declared order.
 */
// eslint-disable-next-line import/prefer-default-export
export const resolveDependencies = ({ dependencies, ...values }) =>
  (dependencies ?? []).map((path) => {
    const value = valueForPath(path, values);
    if (value === undefined) throw new UnresolvedDependencyError(path);
    return { key: path, value };
  });
